import * as React from 'react';
import { connect } from 'react-redux';

import NotifyHOC from '../../../../HOC/Notifications';

import QuestionEditorSwitch from '../QuestionEditorSwitch';
import ConditionEditor from '../ConditionEditor';
import { IApplicationState } from '../../../../reducers';
import { QTYPES, Project, Question } from '@nexxt/common/types';
import { QuestionTypesMap, PREVIEW_MODE } from '@nexxt/common/questionTypes';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import * as projectService from '../../../../services/project.service';
import Loader from '../../../common/Loader/Loader';
import BotPreview from '../../botPreview';
import * as tokenService from '@nexxt/common/firebase/auth/userDetails';
import { BUILDER_STATES } from '../../../../interface/common.interface';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { getConfirmationBox } from '../../../common/swal/Sweetalert';
import { alertMessage } from '../../../../constants/alertMessage';
import ConversationalSettingsTab from '../ConversationalSettingsTab';

const { useState, useEffect } = React;
interface QuestionBuilderProps {
    currentProject: Project;
    currentQuestion: Question;
    previewPackageId: number;
    isLogic: boolean;
    isLoading: boolean;
    isEdited: boolean;
    builderState: BUILDER_STATES;
    isPreviewReady: boolean;
    isNewQuestion: boolean;
    accountURL: string;
    onQuestionPreview: (question) => Promise<void>;
    onQuestionEdit: (question) => Promise<void>;
    onQuestionLogic: (question) => Promise<void>;
}

function QuestionBuilder(props: QuestionBuilderProps) {
    const {
        currentQuestion,
        currentProject,
        builderState,
        previewPackageId,
        isPreviewReady,
        isEdited,
        isNewQuestion,
        accountURL,
        onQuestionPreview,
        onQuestionEdit,
        onQuestionLogic
    } = props;
    const [projectInfo, setProjectInfo] = useState(
        projectService.projectToProjectInfo(props.currentProject)
    );
    const [tabIndex, setTabIndex] = useState<number>(0);

    useEffect(() => {
        if (tabIndex == 2 && !showPreviewTab) {
            setTabIndex(0);
        }
    }, [currentQuestion]);

    useEffect(() => {
        const newProjectInfo = projectService.projectToProjectInfo(
            props.currentProject
        );
        setProjectInfo(newProjectInfo);
    }, [props.currentProject]);

    // handle loader
    if (props.isLoading) {
        // loader
        return <Loader isFixed={true} isInline={true} />;
    }
    const isDefaultQuestion = [QTYPES.GREETING_TEXT, QTYPES.REDIRECT].includes(
        currentQuestion.type
    );
    const isBuiltInAudienceQuestion =
        props.currentQuestion.isAudience && props.currentQuestion.builtInID;
    const showLogicTab =
        !isDefaultQuestion &&
        !isNewQuestion &&
        !isBuiltInAudienceQuestion &&
        currentQuestion?.id;

    const currentQuestionTypeSettings = QuestionTypesMap[currentQuestion.type];
    const showPreviewTab =
        [PREVIEW_MODE.ENABLED, PREVIEW_MODE.BROKEN].includes(
            currentQuestionTypeSettings?.preview
        ) && !isNewQuestion;
    const previewIsBroken =
        currentQuestionTypeSettings?.preview === PREVIEW_MODE.BROKEN;
    const botUrl = process.env.BOT_URL;
    const botPreviewUrl = `${botUrl}/p/${currentProject?.id}_${currentQuestion?.id}_PREVIEW?TERMINATE=msg&testMode=true`;

    const showConversationalSettingsTab =
        currentQuestionTypeSettings?.smartProbe?.enable;

    const checkCurrentTab = (tab: number): string => {
        const enabledTabs = [
            'questionEditor',
            showConversationalSettingsTab ? 'smartprobe' : null,
            showLogicTab ? 'questionLogic' : null,
            showPreviewTab ? 'questionPreview' : null
        ].filter((x) => x);
        return enabledTabs[tab];
    };

    const renderTab = (questionType: string): void => {
        switch (questionType) {
            case 'questionEditor':
            case 'smartprobe':
                onQuestionEdit(currentQuestion);
                break;
            case 'questionLogic':
                onQuestionLogic(currentQuestion);
                break;
            case 'questionPreview':
                onQuestionPreview(currentQuestion);
                break;
        }
    };

    const warningModal = (nextTab: number): void => {
        if (props.isEdited) {
            getConfirmationBox({
                message: alertMessage.CONFIRM_CONTINUE,
                showCloseButton: true,
                confirmButtonColor: '',
                title: alertMessage.UNDO_CURRENT_CHANGE
            }).then((x) => {
                if (x.value) {
                    setTabIndex(nextTab);
                    renderTab(checkCurrentTab(nextTab));
                }
            });
        } else {
            setTabIndex(nextTab);
            renderTab(checkCurrentTab(nextTab));
        }
    };
    return (
        <Tabs
            className="question-builder--tab"
            selectedIndex={tabIndex}
            onSelect={(index) => warningModal(index)}
        >
            <TabList>
                <Tab>Question Editor</Tab>
                {showConversationalSettingsTab && (
                    <Tab>Conversational Settings</Tab>
                )}
                {showLogicTab && <Tab>Question Logic</Tab>}
                {showPreviewTab && <Tab>Question Preview</Tab>}
            </TabList>

            <TabPanel>
                <QuestionEditorSwitch type={currentQuestion.type as QTYPES} />
            </TabPanel>
            {showConversationalSettingsTab && (
                <TabPanel>
                    <ConversationalSettingsTab />
                </TabPanel>
            )}
            {showLogicTab && (
                <TabPanel>
                    <ConditionEditor />
                </TabPanel>
            )}
            {showPreviewTab && (
                <TabPanel>
                    <div className="project-add--question-builder">
                        {builderState === BUILDER_STATES.PREVIEW && (
                            <BotPreview
                                waiting={!isPreviewReady}
                                selectedDevices={currentProject.devices}
                                previewPackageId={previewPackageId}
                                projectInfo={projectInfo}
                                accountURL={accountURL}
                                questionType={currentQuestion.type}
                                isBroken={previewIsBroken}
                                previewLink={botPreviewUrl}
                            />
                        )}
                    </div>
                </TabPanel>
            )}
        </Tabs>
    );
}

const mapStateToProps = (store: IApplicationState) => {
    const {
        projectReducer: { currentProject },
        questionReducer: {
            currentQuestion,
            previewPackageId,
            isLogic,
            isNewQuestion
        },
        utilsReducer: { isEdited }
    } = store;
    return {
        currentProject,
        currentQuestion,
        previewPackageId,
        isLogic,
        isEdited,
        isNewQuestion,
        accountURL: tokenService.getTenantUrl()
    };
};

export default NotifyHOC(connect(mapStateToProps, null)(QuestionBuilder));
